import { Button, ButtonProps, Flex, FlexProps, Text } from '@chakra-ui/core'
import React, { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Search } from './Search'
import { ClearSelectedItems } from './ClearSelectedItems'
import { ListNode } from './ListNode'
import { multipleIdsSelection, singleIdsSelection } from './utils'
import LocaleText from 'components/LocaleText'
import { SkeletonText } from '../SkeletonText'
import keys from 'constants/keys'
import { NoResults } from '../GroupsTree/NoResults'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { RootState } from 'constants/interfaces'
import { ChevronDown } from 'lucide-react'
import { VariableSizeList as List } from 'react-window'

type OptionalChildrenButtonProps = Omit<ButtonProps, 'children'> & { children?: React.ReactNode }
export type Item = {
  id: string
  uid?: string
  name: string
  image?: string
  groups?: string[]
  isHeader?: boolean
  parameter?: string
  ui_parameter?: string
}
interface IProps {
  itemsDataArray: Item[]
  mode?: string
  minW?: string
  menuButtonLiteral?: string
  isLoading?: boolean
  isShowSearch?: boolean
  selectedIds?: string[]
  isShowClear?: boolean
  actionButtonIcon?: any | null
  onSubmit?: (items: string[]) => void
  resettingId?: string
  btnH?: string
  btnW?: string
  left?: string
  controlButtonProps?: OptionalChildrenButtonProps
  menuListProps?: FlexProps
  renderCustomItem?: (item: Item, handleCheckIds: (id: string) => void, checkedIds: string[]) => React.ReactNode | null
  baseItemHeight?: number
}
export const SelectDropdown = memo(
  ({
    itemsDataArray,
    mode = keys.SELECT_MODES.MULTIPLE,
    selectedIds,
    minW = '330px',
    menuButtonLiteral = 'user_level_report_filter',
    isLoading = false,
    isShowSearch = false,
    isShowClear = true,
    actionButtonIcon = null,
    onSubmit,
    resettingId,
    btnH,
    btnW,
    menuListProps,
    controlButtonProps,
    baseItemHeight = 40,
    renderCustomItem,
  }: IProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [checkedIds, setCheckedIds] = useState<string[]>(selectedIds || [])
    const isRtl = useSelector(isRtlSelector)
    const theme = useSelector((state: RootState) => state?.general?.theme)
    const [searchText, setSearchText] = useState<string>('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<List>(null)
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
      // eslint-disable-next-line
    }, [])

    const handleSubmit = () => {
      setIsOpen(false)
      onSubmit?.(checkedIds)
    }
    const handleClearCheckedItems = () => {
      setCheckedIds([])
    }
    const handleOpenSelect = () => {
      setIsOpen((prev) => !prev)
      setCheckedIds(selectedIds || [])
      // Reset expanded items state when opening
      if (!isOpen) {
        setExpandedItems(new Set())
      }
    }

    const handleCheckIds = (currentId: string) => {
      if (mode === keys.SELECT_MODES.SINGLE) {
        setCheckedIds(singleIdsSelection({ checkedIds, currentId }))
      } else {
        setCheckedIds(multipleIdsSelection({ checkedIds, currentId, resettingId, itemsDataArray }))
      }
    }

    const items = useMemo(() => {
      if (searchText && isShowSearch) {
        return itemsDataArray.filter((item: Item) => {
          const lowerSearchText = searchText.toLowerCase()
          const itemNameLower = item.name.toLowerCase()

          if (item.name && item.groups && item.uid) {
            const groupsMatch = item.groups.some((group) => group.toLowerCase().includes(lowerSearchText))
            return itemNameLower.includes(lowerSearchText) || groupsMatch || item.uid.includes(lowerSearchText)
          }

          return itemNameLower.includes(lowerSearchText)
        })
      } else {
        return itemsDataArray
      }
    }, [isShowSearch, itemsDataArray, searchText])

    const controlButtonTitle = useMemo(() => {
      if (selectedIds?.length === 0 || !selectedIds) {
        return <LocaleText text={menuButtonLiteral} />
      }

      const selectedItemName = items.filter((item) => item.id === selectedIds[0])[0]?.name
      if (selectedIds?.length === 1) {
        return (
          <Text>
            <LocaleText text={selectedItemName} />
          </Text>
        )
      }
      if (selectedIds && selectedIds?.length > 1) {
        return (
          <Text>
            <LocaleText text={selectedItemName} /> + {selectedIds.length - 1}
          </Text>
        )
      }
    }, [items, menuButtonLiteral, selectedIds])

    const handleItemToggle = useCallback((index: number) => {
      setExpandedItems((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    }, [])

    const getItemSize = useCallback(
      (index: number) => {
        const item = items[index]
        const baseHeight = baseItemHeight // increased base height for item

        // If item has groups and they are expanded, increase height
        if (item?.groups && expandedItems.has(index)) {
          const groupsText = item.groups.join(',')
          // Estimate number of lines: text length divided by 18 characters (width 200px, average character 11px)
          const charsPerLine = 18
          const lines = Math.max(1, Math.ceil(groupsText.length / charsPerLine))
          const additionalHeight = lines * 22 + 20 // 22px per line + 20px margin
          return baseHeight + additionalHeight
        }

        return baseHeight
      },
      [items, baseItemHeight, expandedItems]
    )

    const resetAfterIndex = useCallback((index: number) => {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index)
      }
    }, [])

    return (
      <Flex pos="relative" ref={dropdownRef}>
        <Button
          isDisabled={isLoading}
          dir={isRtl ? 'rtl' : 'ltr'}
          fontSize="12px"
          borderRadius="10px"
          border="1px solid #E0E0E0"
          minW="unset"
          padding="4px"
          flexDir="row-reverse"
          fontFamily='apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
          w={btnW ? btnW : '100%'}
          p={actionButtonIcon ? '0' : '6px'}
          height={btnH ? btnH : 'auto'}
          bg={selectedIds?.length === 0 || !selectedIds ? 'white' : theme?.elementsColor}
          color={selectedIds?.length === 0 || !selectedIds ? 'black' : 'white'}
          onClick={handleOpenSelect}
          _focus={{ boxShadow: 'none' }}
          _hover={{ opacity: 0.8 }}
          _active={{ opacity: 0.8 }}
          {...controlButtonProps}
        >
          {actionButtonIcon ? (
            actionButtonIcon
          ) : (
            <>
              <ChevronDown />
              <SkeletonText isLoaded={!isLoading}>{controlButtonTitle}</SkeletonText>
            </>
          )}
        </Button>
        {isOpen && (
          <Flex
            flexDir="column"
            borderRadius="5px"
            pos="absolute"
            bg="white"
            top="45px"
            right={isRtl ? 'unset' : '0'}
            left={isRtl ? '0' : 'unset'}
            minW={minW}
            boxShadow="md"
            zIndex={2000}
            alignItems="center"
            {...menuListProps}
          >
            {isShowSearch && <Search searchText={searchText} setSearchText={setSearchText} />}
            {isShowClear && items.length > 0 && (
              <ClearSelectedItems
                handleClearCheckedItems={handleClearCheckedItems}
                disabled={checkedIds.length === 0}
              />
            )}

            {items.length > 0 ? (
              <List
                ref={listRef}
                height={window.innerHeight * 0.3}
                itemCount={items.length}
                itemSize={getItemSize}
                width="100%"
                style={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  marginBottom: '-20px',
                  paddingBottom: '20px',
                }}
              >
                {({ index, style }) => {
                  const item = items[index]
                  return (
                    <div style={style}>
                      <ListNode
                        handleCheckIds={handleCheckIds}
                        checkedIds={checkedIds}
                        item={item}
                        mode={mode}
                        isExpanded={expandedItems.has(index)}
                        onToggle={() => {
                          handleItemToggle(index)
                          // Recalculate height after change
                          setTimeout(() => resetAfterIndex(index), 0)
                        }}
                      />
                    </div>
                  )
                }}
              </List>
            ) : (
              <NoResults />
            )}

            {items.length > 0 && (
              <Button
                w="80%"
                h="25px"
                my="15px"
                isDisabled={isLoading}
                onClick={handleSubmit}
                bg={theme?.elementsColor}
                color="white"
                fontSize="12px"
                borderRadius="12px"
                _hover={{ bg: theme?.elementsColor }}
                _active={{ bg: theme?.elementsColor }}
                _focus={{ boxShadow: 'none' }}
              >
                <LocaleText text="select_group_select_button" />
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    )
  }
)
